// Per-call recordings panel on the Conversation page.
//
// Lists every call that produced a recording for this session, newest
// first. Each row exposes its own Play (MSE-streamed) and Delete (site-
// admin only — server returns 403 if the agent isn't authorized).

// Map a server-returned transcribe error code into the line we surface to
// the agent. Lives outside the component so it doesn't allocate per
// render and stays easy to extend as new codes appear.
function humanReadableTranscribeError(code: string, fallback: string): string {
  switch (code) {
    case 'groq-key-missing':
      return 'Server is missing GROQ_API_KEY — ask an admin to set it.';
    case 'too-large':
    case 'payload-too-large':
      return fallback || 'Audio is too large for the current Groq tier.';
    case 'ffmpeg-missing':
      return 'Server is missing ffmpeg — ask an admin to install it.';
    case 'ffmpeg-failed':
      return 'Audio extraction failed — the recording may be corrupted.';
    case 'upstream-status':
      return 'Groq returned an error. Check the server logs and try again.';
    case 'network':
      return 'Network blip reaching Groq. Try again in a moment.';
    default:
      return fallback || 'Transcription failed.';
  }
}

import { useEffect, useRef, useState } from 'react';
import { FileText, MoreHorizontal, Play, Trash2, Video as VideoIcon, X as XIcon } from 'lucide-react';
import type { SessionId } from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { useConversationsStore } from '../stores/conversations';
import { useRecordingsStore } from '../stores/recordings';
import {
  attachCallMSEPlayer,
  deleteCallRecording,
  getTranscribeStatus,
  listCallRecordings,
  transcribeCallRecording,
  type CallRecording,
  type TranscribeJobState,
} from '../lib/recordings-api';
import { isApiError } from '../lib/api';
import { formatDateTime } from '../lib/datetime';
import { serverOrigin } from '../lib/server-origin';
import styles from './RecordingsPanel.module.css';
import { confirmDialog } from '../stores/confirm';

interface Props {
  sessionId: SessionId;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// Per-row thumbnail. The thumbnail endpoint requires auth (Bearer JWT),
// which a plain <img src> can't carry — fetch via JS with the JWT header
// and feed the result into the <img> as a blob URL. Falls back to a
// generic film icon when the thumbnail doesn't exist (older recordings
// captured before this feature, or upload failures).
function ThumbnailTile({
  sessionId,
  callId,
  onClick,
}: {
  sessionId: SessionId;
  callId: string;
  onClick: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const token = useAgentStore((s) => s.current ? s.current : null);
  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;
    const jwt = useAgentStore.getState().tokens?.accessToken;
    const url =
      `${serverOrigin()}/api/recordings/${encodeURIComponent(sessionId)}` +
      `/calls/${encodeURIComponent(callId)}/thumbnail`;
    fetch(url, { headers: jwt ? { Authorization: `Bearer ${jwt}` } : {} })
      .then(async (res) => {
        if (!res.ok) throw new Error('no-thumbnail');
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // `token` is in deps so a fresh login retries — eslint doesn't see this.
     
  }, [sessionId, callId, token]);
  return (
    <button
      type="button"
      className={styles.thumb}
      onClick={onClick}
      aria-label="Play recording"
      title="Play recording"
    >
      {src && !failed ? (
        <img src={src} alt="" className={styles.thumbImg} aria-hidden />
      ) : (
        <span className={styles.thumbFallback} aria-hidden>
          <VideoIcon size={18} />
        </span>
      )}
    </button>
  );
}

// Recorder writes a chunk every 5s (recorder.ts:TIMESLICE_MS). Duration is
// approximately chunkCount * 5s — the final chunk is partial, so this is
// an upper bound (off by ≤ 5s). Good enough for a row label.
const TIMESLICE_SECONDS = 5;
function approxDuration(chunkCount: number): string {
  const total = chunkCount * TIMESLICE_SECONDS;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function RecordingsPanel({ sessionId }: Props) {
  const hasRecording = useRecordingsStore((s) => s.sessionIds.has(sessionId));
  const role = useAgentStore((s) => s.current?.role);
  const canDelete = role === 'super-admin' || role === 'account-admin' || role === 'agent';

  const [calls, setCalls] = useState<CallRecording[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Per-row playing state — only one playback at a time keeps the panel
  // simple and the network footprint sane.
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Which row's transcript drawer is open. At most one is expanded at a
  // time to keep the panel compact; click the same icon to collapse.
  const [openTranscriptId, setOpenTranscriptId] = useState<string | null>(null);
  // Which row's ellipsis-menu is open. Single value → closing one menu
  // when another opens is automatic.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Tracks which call is currently being transcribed. Used to disable the
  // menu item + show "Transcribing…" in the drawer.
  const [transcribingId, setTranscribingId] = useState<string | null>(null);
  const setCallTranscript = useConversationsStore((s) => s.setCallTranscript);
  // Pull the per-call transcript map straight from the conversations
  // store. Seeded by hydrateConversationExtras on detail load; updated by
  // the future create/edit UI. Reading the map (not the entry) gives us
  // a single stable selector that re-renders when any transcript changes.
  const callTranscripts = useConversationsStore(
    (s) => s.byId.get(sessionId)?.callTranscripts ?? null,
  );
  const blobUrlRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (!hasRecording) {
      setCalls(null);
      return;
    }
    setLoading(true);
    listCallRecordings(sessionId)
      .then((rows) => {
        setCalls(rows);
        setError(null);
      })
      .catch(() => setError('Couldn’t load recordings.'))
      .finally(() => setLoading(false));
  }, [sessionId, hasRecording]);

  // Revoke any blob URL on unmount.
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  // Close the playback modal + row menu on Escape. The modal also closes
  // on backdrop click via its onClick handler below.
  useEffect(() => {
    if (!playingCallId && !openMenuId) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (playingCallId) closePlayback();
      if (openMenuId) setOpenMenuId(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
     
  }, [playingCallId, openMenuId]);

  // Click anywhere outside an open menu closes it. Capture-phase so the
  // listener still fires when the menu items live in deeper React trees.
  useEffect(() => {
    if (!openMenuId) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (target?.closest(`[data-menu-id="${openMenuId}"]`)) return;
      setOpenMenuId(null);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [openMenuId]);

  function closePlayback() {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setPlayingCallId(null);
  }

  async function playCall(callId: string) {
    setError(null);
    setPlayingCallId(callId);
    requestAnimationFrame(async () => {
      const video = videoRef.current;
      if (!video) {
        setPlayingCallId(null);
        return;
      }
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      try {
        const url = await attachCallMSEPlayer(video, sessionId, callId);
        blobUrlRef.current = url;
      } catch {
        setError('Playback failed.');
        setPlayingCallId(null);
      }
    });
  }

  // Polling cadence + safety cap. 2 s feels responsive without hammering
  // the server; 5 min is enough for the longest recording within Groq's
  // free-tier limit (whisper-large-v3-turbo runs at ~200× real-time).
  const POLL_INTERVAL_MS = 2000;
  const POLL_TIMEOUT_MS = 5 * 60 * 1000;
  const pollTimerRef = useRef<number | null>(null);
  const pollAbortRef = useRef<{ aborted: boolean }>({ aborted: false });

  function stopPolling() {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollAbortRef.current.aborted = true;
  }

  function applyTranscribeState(callId: string, job: TranscribeJobState) {
    if (job.state === 'done') {
      setCallTranscript(sessionId, {
        callId: job.transcript.callId,
        body: job.transcript.body,
        provider: job.transcript.provider,
        language: job.transcript.language,
        createdAt: job.transcript.createdAt,
      });
      setTranscribingId((cur) => (cur === callId ? null : cur));
      stopPolling();
    } else if (job.state === 'failed') {
      setError(humanReadableTranscribeError(job.error, job.message));
      setTranscribingId((cur) => (cur === callId ? null : cur));
      stopPolling();
    }
    // running / idle: do nothing — caller schedules the next poll.
  }

  function pollUntilSettled(callId: string) {
    // New token for this polling session so any stragglers from a
    // previous round get ignored. Replace the ref so the closure below
    // sees the same token across iterations.
    const token = { aborted: false };
    pollAbortRef.current = token;
    const startedAt = Date.now();
    async function tick() {
      if (token.aborted) return;
      try {
        const status = await getTranscribeStatus(sessionId, callId);
        if (token.aborted) return;
        if (status.state === 'done' || status.state === 'failed') {
          applyTranscribeState(callId, status);
          return;
        }
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setError('Transcription is taking longer than expected — try again.');
          setTranscribingId((cur) => (cur === callId ? null : cur));
          token.aborted = true;
          return;
        }
      } catch {
        // Network blip — keep trying. Hard failures surface via the
        // server-side job state, not via the poll request itself.
      }
      pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
    }
    pollTimerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
  }

  // Stop any in-flight polling when the panel unmounts.
  useEffect(() => {
    return () => stopPolling();
     
  }, []);

  async function transcribeCall(callId: string) {
    if (transcribingId) return; // a transcription is already in flight
    setError(null);
    setTranscribingId(callId);
    setOpenMenuId(null);
    setOpenTranscriptId(callId);
    try {
      const job = await transcribeCallRecording(sessionId, callId);
      if (job.state === 'done' || job.state === 'failed') {
        applyTranscribeState(callId, job);
        return;
      }
      // state === 'running' — poll until terminal.
      pollUntilSettled(callId);
    } catch (err) {
      let message = 'Could not start the transcription.';
      if (isApiError(err)) {
        const body = err.body as { error?: string; message?: string } | null;
        if (body?.error === 'groq-key-missing') {
          message = 'Server is missing GROQ_API_KEY — ask an admin to set it.';
        } else if (body?.error === 'too-large') {
          message = body.message ?? 'Recording is too large for the transcription tier.';
        } else if (err.status === 404) {
          message = 'No recording on disk for this call.';
        } else if (err.status === 503) {
          message = body?.message ?? 'Transcription is not configured on the server.';
        } else if (body?.message) {
          message = body.message;
        }
      }
      setError(message);
      setTranscribingId(null);
    }
  }

  async function deleteCall(callId: string) {
    if (!(await confirmDialog({ title: 'Delete this call recording?', message: 'This cannot be undone.' }))) return;
    setDeletingId(callId);
    setError(null);
    try {
      await deleteCallRecording(sessionId, callId);
      setCalls((prev) => (prev ?? []).filter((c) => c.callId !== callId));
      if (playingCallId === callId) {
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
        setPlayingCallId(null);
      }
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 403
          ? 'You need site-admin permissions to delete recordings.'
          : 'Delete failed.',
      );
    } finally {
      setDeletingId(null);
    }
  }

  if (!hasRecording) {
    return (
      <div className={styles.empty}>
        <p className={styles.emptyText}>No recording for this conversation.</p>
      </div>
    );
  }

  return (
    <section className={styles.root} aria-labelledby="rec-heading">
      <p id="rec-heading" className={styles.meta}>
        {loading
          ? 'Loading…'
          : calls
            ? `${calls.length} ${calls.length === 1 ? 'call' : 'calls'} recorded`
            : '—'}
      </p>

      {error ? <p className={styles.error}>{error}</p> : null}

      {calls && calls.length > 0 ? (
        <ul className={styles.list}>
          {calls.map((c) => {
            const transcript = callTranscripts?.get(c.callId) ?? null;
            const hasTranscript = !!transcript && transcript.body.trim().length > 0;
            const isTranscriptOpen = openTranscriptId === c.callId;
            return (
              <li
                key={c.callId}
                className={[styles.row, playingCallId === c.callId ? styles.rowActive : ''].join(' ')}
              >
                <div className={styles.rowInner}>
                  <ThumbnailTile
                    sessionId={sessionId}
                    callId={c.callId}
                    onClick={() => void playCall(c.callId)}
                  />
                  <div className={styles.rowMain}>
                    <div className={styles.rowTitle}>{formatDateTime(c.startedAt)}</div>
                    <div className={styles.rowMeta}>
                      ~{approxDuration(c.chunkCount)} · {formatBytes(c.totalBytes)}
                    </div>
                  </div>
                  <div className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => void playCall(c.callId)}
                      disabled={loading || deletingId === c.callId}
                      aria-label="Play recording"
                      title="Play recording"
                    >
                      <Play size={14} aria-hidden />
                    </button>
                    <span className={styles.menuWrap} data-menu-id={c.callId}>
                      <button
                        type="button"
                        className={[
                          styles.iconBtn,
                          openMenuId === c.callId ? styles.iconBtnActive : '',
                        ].join(' ')}
                        onClick={() =>
                          setOpenMenuId(openMenuId === c.callId ? null : c.callId)
                        }
                        aria-haspopup="menu"
                        aria-expanded={openMenuId === c.callId}
                        aria-label="More actions"
                        title="More actions"
                      >
                        <MoreHorizontal size={14} aria-hidden />
                      </button>
                      {openMenuId === c.callId ? (
                        <div className={styles.menu} role="menu">
                          {hasTranscript ? (
                            <button
                              type="button"
                              role="menuitem"
                              className={styles.menuItem}
                              onClick={() => {
                                setOpenTranscriptId(
                                  isTranscriptOpen ? null : c.callId,
                                );
                                setOpenMenuId(null);
                              }}
                            >
                              <FileText size={14} aria-hidden />
                              <span>
                                {isTranscriptOpen ? 'Hide transcript' : 'View transcript'}
                              </span>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            role="menuitem"
                            className={styles.menuItem}
                            disabled={transcribingId === c.callId || !!transcribingId}
                            onClick={() => void transcribeCall(c.callId)}
                          >
                            <FileText size={14} aria-hidden />
                            <span>
                              {transcribingId === c.callId
                                ? 'Transcribing…'
                                : hasTranscript
                                  ? 'Regenerate transcript'
                                  : 'Generate transcript'}
                            </span>
                          </button>
                          {canDelete ? (
                            <>
                              <span className={styles.menuDivider} />
                              <button
                                type="button"
                                role="menuitem"
                                className={[
                                  styles.menuItem,
                                  styles.menuItemDanger,
                                ].join(' ')}
                                disabled={deletingId === c.callId}
                                onClick={() => {
                                  setOpenMenuId(null);
                                  void deleteCall(c.callId);
                                }}
                              >
                                <Trash2 size={14} aria-hidden />
                                <span>Delete recording</span>
                              </button>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </span>
                  </div>
                </div>
                {isTranscriptOpen ? (
                  <div className={styles.transcript}>
                    <div className={styles.transcriptHead}>
                      <span>Transcript</span>
                      <span>
                        {hasTranscript
                          ? `${transcript!.provider}${transcript!.language ? ` · ${transcript!.language}` : ''} · ${formatDateTime(transcript!.createdAt)}`
                          : transcribingId === c.callId
                            ? 'Transcribing…'
                            : 'Not yet generated'}
                      </span>
                    </div>
                    {hasTranscript ? (
                      <p className={styles.transcriptBody}>{transcript!.body}</p>
                    ) : transcribingId === c.callId ? (
                      <p className={styles.transcriptEmpty}>
                        Uploading the recording to Groq and waiting for the
                        transcript. This usually finishes in a few seconds for
                        short calls.
                      </p>
                    ) : (
                      <p className={styles.transcriptEmpty}>
                        No transcript for this call yet — pick
                        “Generate transcript” from the menu to run it through
                        Groq.
                      </p>
                    )}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {playingCallId ? (
        <div
          className={styles.modalBackdrop}
          role="dialog"
          aria-modal="true"
          aria-label="Recording playback"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePlayback();
          }}
        >
          <div className={styles.modal}>
            <header className={styles.modalHead}>
              <div>
                <h3 className={styles.modalTitle}>Recording playback</h3>
                <p className={styles.modalSub}>
                  {(() => {
                    const c = (calls ?? []).find((x) => x.callId === playingCallId);
                    return c ? formatDateTime(c.startedAt) : '—';
                  })()}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={closePlayback}
                aria-label="Close playback"
                title="Close"
              >
                <XIcon size={14} aria-hidden />
              </button>
            </header>
            <video
              ref={videoRef}
              className={styles.player}
              controls
              autoPlay
              aria-label="Playback"
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
